import React, { useState, useEffect } from 'react';
import { Upload, Flex, InputNumber, Form, Alert, Progress, Typography, Image } from 'antd';
import { InboxOutlined, VideoCameraOutlined } from '@ant-design/icons';
import GreenLightButton from '../../../components/UI/Buttons/GreenLightButton';
import LspivService from '../../../services/LspivService';
import classes from '../lspiv.module.css';

const { Dragger } = Upload;
const { Title, Text } = Typography;

const UploadStep = ({ jobId, job, onRefresh }) => {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    const [extracting, setExtracting] = useState(false);

    const [form] = Form.useForm();

    const videoFilename = job?.status?.video_filename;
    const frames = job?.artifacts?.filter((a) => a.startsWith('video')) || [];
    const framesCount = job?.frames_count || 0;

    const handleUpload = async (file) => {
        setUploading(true);
        setError(null);
        setProgress(0);
        try {
            await LspivService.uploadVideo(jobId, file, setProgress);
            await onRefresh();
        } catch (err) {
            setError(err?.response?.data?.detail || 'Ошибка загрузки');
        } finally {
            setUploading(false);
        }
        return false; // prevent antd default upload
    };

    const handleExtract = async (values) => {
        setExtracting(true);
        setError(null);
        try {
            await LspivService.extractFrames(jobId, {
                start_frame: values.start_frame || 0,
                end_frame: values.end_frame || null,
                every: values.every || 1,
                resize_factor: values.resize_factor || 1.0,
            });
            setTimeout(onRefresh, 1500);
        } catch (err) {
            setError(err?.response?.data?.detail || 'Ошибка запуска извлечения');
        } finally {
            setExtracting(false);
        }
    };

    return (
        <Flex vertical gap={20}>
            <Title level={4} style={{ margin: 0 }}>
                <VideoCameraOutlined /> Видео и извлечение кадров
            </Title>
            <Text type="secondary">
                Загрузите видео реки. Затем извлеките из него кадры с нужным интервалом.
                Для многопроходного PIV подойдут 30–100 кадров.
            </Text>

            {error && <Alert type="error" message={error} showIcon />}

            {!videoFilename && (
                <Dragger
                    beforeUpload={handleUpload}
                    showUploadList={false}
                    accept="video/*"
                    disabled={uploading}
                    style={{ borderRadius: 8 }}
                >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">Перетащите видео сюда или нажмите для выбора</p>
                    <p className="ant-upload-hint">Поддерживаются MP4, MOV, AVI</p>
                    {uploading && <Progress percent={progress} style={{ maxWidth: 400, margin: '12px auto' }} />}
                </Dragger>
            )}

            {videoFilename && (
                <Alert
                    type="success"
                    message="Видео загружено"
                    description={videoFilename}
                    showIcon
                />
            )}

            {videoFilename && (
                <Form form={form} layout="vertical" onFinish={handleExtract} initialValues={{
                    start_frame: 0,
                    end_frame: null,
                    every: 1,
                    resize_factor: 1.0,
                }}>
                    <Flex gap={16} wrap="wrap" align="flex-end">
                        <Form.Item name="start_frame" label="Начальный кадр" style={{ minWidth: 140 }}>
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="end_frame" label="Конечный кадр (null = до конца)" style={{ minWidth: 180 }}>
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="every" label="Брать каждый N-й" style={{ minWidth: 140 }}>
                            <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="resize_factor" label="Resize factor (0.1..1)" style={{ minWidth: 160 }}>
                            <InputNumber min={0.1} max={1.0} step={0.1} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item>
                            <GreenLightButton htmlType="submit" loading={extracting}>
                                Извлечь кадры
                            </GreenLightButton>
                        </Form.Item>
                    </Flex>
                </Form>
            )}

            {framesCount > 0 && (
                <>
                    <Text strong>Извлечено кадров: {framesCount}</Text>
                    <FramesPreview jobId={jobId} />
                </>
            )}
        </Flex>
    );
};

const FramesPreview = ({ jobId }) => {
    const [frames, setFrames] = useState([]);
    const [err, setErr] = useState(null);

    useEffect(() => {
        LspivService.listFrames(jobId).then(setFrames).catch((e) => setErr(e.message));
    }, [jobId]);

    if (err) return <Alert type="warning" message={err} />;
    if (frames.length === 0) return <Text type="secondary">Кадры ещё не извлечены</Text>;

    return (
        <div className={classes.framesGrid}>
            {frames.slice(0, 48).map((name) => (
                <Image
                    key={name}
                    src={LspivService.frameUrl(jobId, name)}
                    alt={name}
                    className={classes.frameThumb}
                    preview={{ src: LspivService.frameUrl(jobId, name) }}
                />
            ))}
        </div>
    );
};

export default UploadStep;
